/**
 * Health insurance provider lists for the insurance screen (datalist autofill).
 * Region is determined by geolocation.country: 'AU' → Australia list, else US list.
 */

import { hasReferralInURL } from './AssessmentUtils';

// Special dropdown options that appear first in InsuranceProvider screen
export const NOT_LISTED_OPTION = 'My insurer is not listed here';

// Referred users get the larger insurance discount.
const INSURANCE_DISCOUNT = {
  direct: { couponCode: 'INRU15', displayPercent: '15%' },
  referred: { couponCode: 'INRU30', displayPercent: '30%' },
} as const;

export function getInsuranceDiscount(): {
  couponCode: `INRU${15 | 30}`;
  displayPercent: `${15 | 30}%`;
} {
  return hasReferralInURL() ? INSURANCE_DISCOUNT.referred : INSURANCE_DISCOUNT.direct;
}

// Health cover type options for the Medicare Access flow
export const HEALTH_COVER_MEDICARE_ORIGINAL = 'Original Medicare';
export const HEALTH_COVER_MEDICARE_ADVANTAGE = 'Medicare Advantage';
export const HEALTH_COVER_OTHER = 'Other';

/**
 * Stored values for the under-65 coverage plan-type question. They match hydra-data-types'
 * INSURANCE_COVERAGE_CHECK_PLAN_TYPES so web-assessment answers line up with the coverage-check
 * leads; the two Medicare values are the HEALTH_COVER_* constants above so isMedicareOriginal
 * reads both screens the same way. Labels shown to the user live in CoverageConfig.ts.
 */
export const COVERAGE_PLAN_TYPE_EMPLOYER = 'Through employer';
export const COVERAGE_PLAN_TYPE_SELF_PURCHASED = 'Self-purchased';
export const COVERAGE_PLAN_TYPE_MEDICAID = 'Medicaid';
export const COVERAGE_PLAN_TYPE_NOT_SURE = 'Not sure';

/** Exclusive answers on the coverage benefits multi-select. */
export const COVERAGE_BENEFIT_NONE = 'None of these';
export const COVERAGE_BENEFIT_NOT_SURE = 'I’m not sure';

// Medicare Advantage plan providers, ordered by membership size.
export const MEDICARE_ADVANTAGE_PROVIDERS: string[] = [
  'UnitedHealthcare',
  'Humana',
  'Aetna (CVS Health)',
  'Anthem (Elevance Health)',
  'Kaiser Permanente',
  'Centene / WellCare',
  'Health Care Service Corporation',
  'Cigna Healthcare',
  'Blue Cross Blue Shield of Michigan',
  'UPMC Health Plan',
  'SCAN Health Plan',
  'Devoted Health',
  'Florida Blue (Blue Cross Blue Shield of Florida)',
  'Highmark Blue Cross Blue Shield',
  'Independence Blue Cross',
  'Blue Shield of California',
  'Geisinger Health Plan',
  'Alignment Healthcare',
  'Horizon Blue Cross Blue Shield of New Jersey',
  'SelectHealth (Intermountain Healthcare)',
  'Healthfirst (New York)',
  'Blue Cross Blue Shield of Tennessee',
  'Medical Mutual of Ohio',
  'Priority Health (Michigan)',
  'Excellus Blue Cross Blue Shield',
  'Blue Cross Blue Shield of North Carolina',
  'Sentara Health Plans (formerly Optima Health)',
  'CareFirst Blue Cross Blue Shield',
  'Independent Health (New York)',
  'MVP Health Care',
  'Clover Health',
  'Essence Healthcare',
  "Capital District Physicians' Health Plan (CDPHP)",
  'Providence Health Plan',
  'Banner Health',
  'Network Health (Wisconsin)',
  "Martin's Point Health Care",
  'Medica',
  'Regence (Cambia Health Solutions)',
  'Capital Blue Cross',
  'EmblemHealth',
  'Quartz Health Solutions',
  'Wellmark Blue Cross Blue Shield',
  'PacificSource Health Plans',
  'Molina Healthcare',
  'Brand New Day (Universal Care)',
  'Blue Cross of Idaho',
  'Premera Blue Cross',
  'Imperial Health Plan of California',
  'Zing Health',
];

// Stored insuranceType answers that the insurance coverage selectors check
export const INSURANCE_TYPE_PUBLIC = 'Public / government program (e.g. Medicare, NHS, Medicaid)';
export const INSURANCE_TYPE_NONE = 'I do not have health cover';

export const INSURANCE_PROVIDERS_US: string[] = [
  'AARP (UnitedHealthcare)',
  'Aetna (CVS Health)',
  'Aflac',
  'Alignment Healthcare',
  'Allstate Health Solutions',
  'AlohaCare',
  'Ambetter (Centene)',
  'American Fidelity',
  'AmeriHealth / AmeriHealth Caritas',
  'Anthem Blue Cross (California)',
  'Anthem Blue Cross Blue Shield (CO, CT, GA, IN, KY, ME, MO, NV, NH, OH, VA, WI)',
  'Arkansas Blue Cross Blue Shield',
  'Asuris Northwest Health',
  'AultCare',
  'AvMed',
  'Blue Cross Blue Shield (General)',
  'Blue Cross Blue Shield of Alabama',
  'Blue Cross Blue Shield of Arizona',
  'Blue Cross Blue Shield of Georgia',
  'Blue Cross Blue Shield of Illinois',
  'Blue Cross Blue Shield of Kansas / Kansas City',
  'Blue Cross Blue Shield of Louisiana',
  'Blue Cross Blue Shield of Massachusetts',
  'Blue Cross Blue Shield of Michigan',
  'Blue Cross Blue Shield of Minnesota',
  'Blue Cross Blue Shield of Mississippi',
  'Blue Cross Blue Shield of Montana',
  'Blue Cross Blue Shield of Nebraska',
  'Blue Cross Blue Shield of New Mexico',
  'Blue Cross Blue Shield of North Carolina',
  'Blue Cross Blue Shield of Oklahoma',
  'Blue Cross Blue Shield of Rhode Island',
  'Blue Cross Blue Shield of South Carolina',
  'Blue Cross Blue Shield of Tennessee',
  'Blue Cross Blue Shield of Texas',
  'Blue Cross Blue Shield of Vermont',
  'Blue Cross Blue Shield of Wyoming',
  'Blue Shield of California',
  'Bright Health',
  'Cambia Health Solutions',
  'CareFirst BlueCross BlueShield (MD, DC, VA)',
  'CareOregon',
  'CareSource',
  'Centene Corporation',
  'CHAMPVA',
  'CHIP (Children’s Health Insurance Program)',
  'Cigna (The Cigna Group)',
  'Clover Health',
  'Community Health Choice',
  'ConnectiCare',
  'Dean Health Plan',
  'Delta Dental',
  'Devoted Health',
  'Elevance Health',
  'EmblemHealth (GHI/HIP)',
  'Empire BlueCross BlueShield (New York)',
  'Excellus BlueCross BlueShield',
  'Fallon Health',
  'Florida Blue',
  'Geisinger Health Plan',
  'Golden Rule Insurance',
  'Harvard Pilgrim Health Care',
  'Health Care Service Corporation (HCSC)',
  'Health Net',
  'HealthPartners',
  'Highmark (PA, DE, WV, NY)',
  'Horizon Blue Cross Blue Shield of New Jersey',
  'Humana',
  'Independence Blue Cross (PA)',
  'Independent Health',
  'Indian Health Service (IHS)',
  'Kaiser Permanente',
  'L.A. Care Health Plan',
  'LifeWise Health Plan',
  'Magellan Health',
  'Mass General Brigham Health Plan',
  'Medica',
  'Medicaid (State-funded)',
  'Medical Mutual of Ohio',
  HEALTH_COVER_MEDICARE_ORIGINAL,
  'Medicare Advantage (Private)',
  'Meridian Health Plan',
  'MetroPlusHealth',
  'Molina Healthcare',
  'Mutual of Omaha',
  'MVP Health Care',
  'Neighborhood Health Plan',
  'Oscar Health',
  'PacificSource Health Plans',
  'Paramount Health Care',
  'Premera Blue Cross (WA, AK)',
  'Priority Health',
  'Providence Health Plans',
  'Quartz Health Solutions',
  'Regence BlueShield (ID, OR, UT, WA)',
  'Sanford Health Plan',
  'SelectHealth',
  'Sentara Healthcare',
  'Sharp Health Plan',
  'SummaCare',
  'Sutter Health Plus',
  'TRICARE (Military)',
  'Tufts Health Plan',
  'UCare',
  'UnitedHealthcare',
  'UPMC Health Plan',
  'VA Health Care (Veterans Affairs)',
  'WellCare (Centene)',
  'Wellmark Blue Cross Blue Shield (IA, SD)',
  'WPS Health Insurance',
];

/** US providers without Original Medicare — for screens only reached by non-Original users. */
export const INSURANCE_PROVIDERS_US_NON_ORIGINAL: string[] = INSURANCE_PROVIDERS_US.filter(
  (provider) => provider !== HEALTH_COVER_MEDICARE_ORIGINAL
);

export const INSURANCE_PROVIDERS_US_MEDICARE_FIRST: string[] = [
  HEALTH_COVER_MEDICARE_ORIGINAL,
  ...INSURANCE_PROVIDERS_US_NON_ORIGINAL,
];

export const INSURANCE_PROVIDERS_AU: string[] = [
  'AAMI Health Insurance (nib)',
  'ACA Health Benefits Fund',
  'ahm health insurance (Medibank)',
  'AIA Health Insurance',
  'Allianz Care',
  'Apia (nib)',
  'Astute Simplicity Health',
  'Australian Unity Health',
  'BankSA (Bupa)',
  'Bupa',
  'CBHS Corporate Health',
  'CBHS Health Fund',
  'CDH Benefits Fund',
  'CUA Health (Great Southern Bank)',
  'Defence Health',
  'Department of Veterans’ Affairs (DVA)',
  'Doctors’ Health Fund',
  'Emergency Services Health',
  'Frank Health Insurance (GMHBA)',
  'GIO Health Insurance (nib)',
  'GMHBA',
  'GU Health',
  'HBF',
  'HCF (Hospitals Contribution Fund)',
  'HCi (Health Care Insurance)',
  'Health Insurance Fund of Australia (HIF)',
  'Health Partners',
  'Hunter Health Insurance',
  'IMAN Australian Health Plans',
  'ING Health Insurance (nib)',
  'Kogan Health',
  'Latrobe Health Services',
  'Medibank Private',
  'Medicare',
  'Mildura Health Fund',
  'MyOwn (AIA)',
  'Navy Health',
  'nib Health Funds',
  'Nurses & Midwives Health',
  'Onemedifund',
  'Peoplecare Health Insurance',
  'Phoenix Health Fund',
  'Police Health',
  'Priceline Health Insurance (nib)',
  'Qantas Health Insurance (nib)',
  'Queensland Country Health Fund',
  'RAC Health Insurance (nib)',
  'Reserve Bank Health Society',
  'RT Health',
  'see-u (HBF)',
  'St Lukes',
  'Suncorp (nib)',
  'Teachers Health',
  'Transport Health',
  'TUH (Teachers Union Health)',
  'UniHealth',
  'Union Health',
  'Westfund Health Insurance',
];
